import { useEffect } from "react";
import { delegate, hideAll } from "tippy.js";
import { TOOLTIP_BUTTON_CLASS } from "../../constants/tooltipButtonClass";

export function useDelegatedMenuTooltips(menuRef: React.RefObject<HTMLElement | null>) {
    useEffect(() => {
        const menuRoot = menuRef.current;

        if (!menuRoot) return;

        const delegated = delegate(menuRoot, {
            target: `.${TOOLTIP_BUTTON_CLASS}:not([disabled])`,
            content(ref) {
                return (ref as HTMLElement).dataset.tooltip || "";
            },
            onShow(instance) {
                const ref = instance.reference as HTMLElement;
                const next = ref.dataset.tooltip || "";

                instance.setContent(next);

                if (!next.trim()) {
                    return false;
                }

                return;
            },
            allowHTML: true,
            animation: false,
            duration: 0,
            delay: [200, 0],
            placement: "top",
            appendTo: () => document.body,
            popperOptions: { strategy: "fixed" },
        });

        const observer = new MutationObserver(() => {
            hideAll({ duration: 0 });
        });

        observer.observe(menuRoot, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ["disabled", "data-tooltip"],
        });

        const onPointerDownCapture = () => hideAll({ duration: 0 });
        menuRoot.addEventListener("pointerdown", onPointerDownCapture, true);

        return () => {
            menuRoot.removeEventListener("pointerdown", onPointerDownCapture, true);
            observer.disconnect();
            delegated.destroy();
        };
    }, [menuRef]);
}
